// Fills the IRS Form 1099-NEC Copy B PDF shipped at /public/1099NEC.pdf
// with driver + payer + box 1 data, then downloads it.
//
// Two paths are attempted:
//   1. If the PDF is an AcroForm (has fillable fields), set the fields by
//      best-guess name match. Names vary across IRS revisions, so we do
//      case-insensitive substring matching and log whatever is there.
//   2. Fallback: draw text directly on page 1 at approximate coordinates.
//      Coordinate constants live at the top of this file — tweak them if
//      a printed form shows misaligned text and the positions move uniformly.
//
// No dependency on jsPDF — uses pdf-lib exclusively.

import { PDFDocument, StandardFonts, rgb, type PDFTextField } from 'pdf-lib';

export interface DriverTaxInfo {
  recipient_tin?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  // Legacy fallback: one concatenated "City, ST ZIP" string. Prefer the
  // separate fields above when both are present.
  city_state_zip?: string | null;
}

export interface PayerInfo {
  payer_tin?: string | null;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  city_state_zip?: string | null; // legacy
  phone?: string | null;
}

export interface Fill1099NecInput {
  driverLabel: string;
  year: number;
  totalPaid: number;
  taxInfo?: DriverTaxInfo | null;
  payerInfo?: PayerInfo | null;
  templateUrl?: string; // defaults to '/1099NEC.pdf'
}

// Fallback overlay coordinates. PDF origin is bottom-left; letter page is
// 612 × 792 pt. These target the TOP Copy B on the standard IRS blank form
// shipped at /public/1099NEC.pdf. Form rows are ~30pt tall; if a printed
// form shows all text shifted uniformly, adjust these constants together.
// Calibrated against /public/1099NEC.pdf. The IRS template prints each row's
// label at the TOP of its box; values go underneath in the open area.
// Tweak in pairs if the printed form drifts — row spacing is ~30pt.
const COORDS = {
  payerBlockX: 52,
  payerBlockYTop: 728, // PAYER name baseline (row 1, top-left)
  payerBlockLineHeight: 11,

  // "For calendar year" box on right side.
  yearX: 505,
  yearY: 692,

  // Row 2: PAYER'S TIN | RECIPIENT'S TIN | Box 1 Nonemployee compensation.
  payerTinX: 70,
  payerTinY: 665,

  recipientTinX: 215,
  recipientTinY: 665,

  // Leave room for the template's preprinted "$" glyph in Box 1.
  box1X: 355,
  box1Y: 665,

  // Row 3: RECIPIENT'S name (left).
  recipientNameX: 55,
  recipientNameY: 632,

  // Row 4: Street address (left).
  streetAddressX: 55,
  streetAddressY: 602,

  // Row 5: City / state / ZIP (left) — assembled into one line.
  cityStateZipX: 55,
  cityStateZipY: 572,
};

// The IRS template already prints a "$" inside Box 1, so we only draw the
// numeric value — otherwise the output shows "$$7,832.00".
const money = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Build the filled 1099-NEC PDF as raw bytes. Use when you want to preview
// the PDF (in an iframe) without forcing a download.
export async function build1099NecPdfBytes(input: Fill1099NecInput): Promise<Uint8Array> {
  const templateUrl = input.templateUrl || '/1099NEC.pdf';

  const res = await fetch(templateUrl);
  if (!res.ok) throw new Error(`Failed to load 1099-NEC template at ${templateUrl}`);
  const bytes = await res.arrayBuffer();

  const pdfDoc = await PDFDocument.load(bytes);
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const { taxInfo, payerInfo, year, totalPaid, driverLabel } = input;

  // ----- Try AcroForm field fill first ---------------------------------
  let filledViaForm = false;
  try {
    const form = pdfDoc.getForm();
    const fields = form.getFields();
    if (fields.length > 0) {
      const fieldMap: Record<string, PDFTextField> = {};
      for (const f of fields) {
        const name = f.getName();
        if (f.constructor.name === 'PDFTextField') {
          fieldMap[name.toLowerCase()] = f as PDFTextField;
        }
      }
      // Track the critical fields so we only "trust" the AcroForm path if
      // both the Box 1 amount AND the recipient name landed somewhere. A
      // partial match (e.g. only 'year') shouldn't suppress the drawing
      // fallback — that's how we shipped a half-empty form before.
      let hitBox1 = false;
      let hitRecipient = false;
      const trySet = (needle: string, value: string | null | undefined, critical?: 'box1' | 'recipient') => {
        if (!value) return;
        const hit = Object.keys(fieldMap).find(n => n.includes(needle));
        if (hit) {
          try {
            fieldMap[hit].setText(value);
            if (critical === 'box1') hitBox1 = true;
            if (critical === 'recipient') hitRecipient = true;
          } catch { /* ignore */ }
        }
      };
      // Heuristic matches — adjust once real names are known.
      trySet('payer', [payerInfo?.name, payerInfo?.address, payerInfo?.city_state_zip, payerInfo?.phone].filter(Boolean).join('\n'));
      trySet("payer's tin", payerInfo?.payer_tin);
      trySet('payer_tin', payerInfo?.payer_tin);
      trySet("recipient's tin", taxInfo?.recipient_tin);
      trySet('recipient_tin', taxInfo?.recipient_tin);
      trySet("recipient's name", driverLabel, 'recipient');
      trySet('recipient_name', driverLabel, 'recipient');
      trySet('street', taxInfo?.street_address);
      trySet('address', taxInfo?.street_address);
      trySet('city', taxInfo?.city_state_zip);
      trySet('nonemployee', money(totalPaid), 'box1');
      trySet('box 1', money(totalPaid), 'box1');
      trySet('calendar year', String(year));
      trySet('year', String(year));

      if (hitBox1 && hitRecipient) {
        filledViaForm = true;
        // Flatten so fields become permanent (not editable) in the output.
        form.flatten();
      }
    }
  } catch {
    // No AcroForm — fall through to drawing path.
  }

  // ----- Fallback: draw text on page 1 at approximate coordinates -------
  if (!filledViaForm) {
    const page = pdfDoc.getPages()[0];
    const draw = (text: string, x: number, y: number, size = 10, bold = false) => {
      page.drawText(text, {
        x,
        y,
        size,
        font: bold ? helveticaBold : helvetica,
        color: rgb(0, 0, 0),
      });
    };

    // Assemble "City, ST ZIP" from separate fields when present, else use
    // the legacy concatenated string.
    const assembleCityStateZip = (
      parts: { city?: string | null; state?: string | null; zip_code?: string | null; city_state_zip?: string | null }
    ) => {
      const fromParts = [parts.city, parts.state, parts.zip_code].filter(Boolean).join(parts.state ? ' ' : ', ');
      if (parts.city && parts.state && parts.zip_code) {
        return `${parts.city}, ${parts.state} ${parts.zip_code}`;
      }
      return fromParts || parts.city_state_zip || '';
    };

    // Payer block (name bold, others normal)
    if (payerInfo) {
      let yp = COORDS.payerBlockYTop;
      if (payerInfo.name) { draw(payerInfo.name, COORDS.payerBlockX, yp, 10, true); yp -= COORDS.payerBlockLineHeight; }
      if (payerInfo.address) { draw(payerInfo.address, COORDS.payerBlockX, yp, 9); yp -= COORDS.payerBlockLineHeight; }
      const payerCsz = assembleCityStateZip(payerInfo);
      if (payerCsz) { draw(payerCsz, COORDS.payerBlockX, yp, 9); yp -= COORDS.payerBlockLineHeight; }
      if (payerInfo.phone) { draw(payerInfo.phone, COORDS.payerBlockX, yp, 9); }
    }

    // For calendar year
    draw(String(year), COORDS.yearX, COORDS.yearY, 12, true);

    // TINs + Box 1
    if (payerInfo?.payer_tin) draw(payerInfo.payer_tin, COORDS.payerTinX, COORDS.payerTinY, 10);
    if (taxInfo?.recipient_tin) draw(taxInfo.recipient_tin, COORDS.recipientTinX, COORDS.recipientTinY, 10);
    draw(money(totalPaid), COORDS.box1X, COORDS.box1Y, 11, true);

    // Recipient info
    draw(driverLabel, COORDS.recipientNameX, COORDS.recipientNameY, 10, true);
    if (taxInfo?.street_address) draw(taxInfo.street_address, COORDS.streetAddressX, COORDS.streetAddressY, 9);
    const recipientCsz = taxInfo ? assembleCityStateZip(taxInfo) : '';
    if (recipientCsz) draw(recipientCsz, COORDS.cityStateZipX, COORDS.cityStateZipY, 9);
  }

  return await pdfDoc.save();
}

export async function fill1099NecPdf(input: Fill1099NecInput): Promise<void> {
  const outBytes = await build1099NecPdfBytes(input);
  const blob = new Blob([outBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `1099_NEC_${input.year}_${input.driverLabel.replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
